import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { cn } from "../../../lib/utils.js";
import type { AddPhoneWizardStep } from "./add-phone-wizard-state.js";

const PROGRESS_STEPS = ["business", "phone", "verify", "connect"] as const;

export function addPhoneProgressIndex(step: AddPhoneWizardStep): number {
  switch (step) {
    case "BUSINESS_PROFILE":
      return 1;
    case "PHONE_NUMBER":
      return 2;
    case "VERIFICATION_METHOD":
    case "VERIFICATION_CODE":
      return 3;
    case "CONNECTING":
    case "READY_TO_ATTACH":
    case "REGISTRATION_PENDING":
    case "SUCCESS":
      return 4;
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}

export function AddPhoneProgress({ step }: { step: AddPhoneWizardStep }): React.JSX.Element {
  const { t } = useTranslation();
  const current = addPhoneProgressIndex(step);
  const currentLabel = t(`whatsapp.addPhone.progressSteps.${PROGRESS_STEPS[current - 1]}`);

  return (
    <nav aria-label={t("whatsapp.addPhone.progress", { step: current })}>
      <ol className="m-0 grid list-none grid-cols-4 gap-1 p-0">
        {PROGRESS_STEPS.map((key, index) => {
          const number = index + 1;
          const state =
            step === "SUCCESS" || number < current
              ? "complete"
              : number === current
                ? "current"
                : "upcoming";
          const label = t(`whatsapp.addPhone.progressSteps.${key}`);
          return (
            <li key={key} className="grid min-w-0 justify-items-center gap-1.5 text-center">
              <span className="relative flex h-7 w-full items-center justify-center">
                {index > 0 ? (
                  <span
                    className={cn(
                      "absolute inset-s-0 end-1/2 top-1/2 h-px",
                      number <= current ? "bg-primary" : "bg-line",
                    )}
                    aria-hidden="true"
                  />
                ) : null}
                {index < PROGRESS_STEPS.length - 1 ? (
                  <span
                    className={cn(
                      "absolute start-1/2 inset-e-0 top-1/2 h-px",
                      number < current ? "bg-primary" : "bg-line",
                    )}
                    aria-hidden="true"
                  />
                ) : null}
                <span
                  className={cn(
                    "relative z-10 flex size-7 items-center justify-center rounded-full text-[0.75rem] font-semibold",
                    state === "complete" && "bg-primary text-primary-foreground",
                    state === "current" && "border-2 border-primary bg-surface text-primary",
                    state === "upcoming" && "border border-line bg-surface text-ink-muted",
                  )}
                  aria-current={state === "current" ? "step" : undefined}
                >
                  {state === "complete" ? <Check size={14} aria-hidden="true" /> : number}
                </span>
              </span>
              <span
                className={cn(
                  "max-w-full text-[0.65rem] leading-tight font-medium",
                  state === "upcoming" ? "text-ink-muted" : "text-ink",
                )}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
      <p className="sr-only">{t("whatsapp.addPhone.progressLabel", { step: current, label: currentLabel })}</p>
    </nav>
  );
}
